import React from 'react';
import {
  Card,
  Row,
  Col,
  Space,
  Typography,
  Tag,
  Spin,
  Alert,
  Divider,
  Table,
  Statistic,
  Button,
  Tooltip
} from 'antd';
import {
  BankOutlined,
  DollarOutlined,
  StockOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  RobotOutlined,
  EditOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * 계좌별 상세 정보를 표시하는 컴포넌트
 */
const AccountDetailCard = ({
  account,
  accountData,
  holdings,
  holdingsData,
  isLoading,
  isRefreshing,
  accountTimestamp,
  hideAmounts,
  onRefresh,
  onEditManualCash,
  onDollarCostAveraging,
  onAIAnalysis
}) => {
  const hasError = accountData?.error;
  const isManualCashEditable = account.broker === 'kis' || (account.broker === 'nh' && ['nh_dc', 'nh_pension'].includes(account.accountId));
  const isKisAccount = account.broker === 'kis';
  const manualCmaBalance = accountData?.manual_cma_balance || 0;

  // 계좌별 금일 손익 계산
  const getTodayPnL = () => {
    let todayPnL = 0;
    holdings.forEach(holding => {
      if (holding.prev_day_change !== null && holding.prev_day_change !== undefined && holding.quantity) {
        todayPnL += holding.prev_day_change * holding.quantity;
      }
    });
    return todayPnL;
  };

  // 보유 주식 테이블 컬럼 정의
  const holdingsColumns = [
    {
      title: '종목명',
      dataIndex: 'stock_name',
      key: 'stock_name',
      width: 150,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.symbol}
            </Text>
          </div>
          <Space size={4}>
            <Button
              size="small"
              onClick={() => onDollarCostAveraging(record)}
              style={{ 
                backgroundColor: '#52c41a',
                borderColor: '#52c41a',
                color: 'white',
                fontSize: '11px',
                padding: '2px 6px',
                height: '22px',
                lineHeight: '18px'
              }}
            >
              물타기
            </Button>
            <Button
              size="small"
              icon={<RobotOutlined />}
              onClick={() => onAIAnalysis(record)}
              style={{ 
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                color: 'white',
                fontSize: '11px',
                padding: '2px 6px',
                height: '22px',
                lineHeight: '18px'
              }}
              title="AI 주가 분석"
            >
              AI
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: '수량',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'right',
      render: (quantity) => quantity?.toLocaleString() || 0,
    },
    {
      title: '평균단가',
      dataIndex: 'avg_buy_price',
      key: 'avg_buy_price',
      width: 100,
      align: 'right',
      render: (price) => price ? `₩${Math.round(price).toLocaleString()}` : '-',
    },
    {
      title: '현재가',
      dataIndex: 'current_price',
      key: 'current_price',
      width: 100,
      align: 'right',
      render: (price) => price ? `₩${Math.round(price).toLocaleString()}` : '-',
    },
    {
      title: '전일대비',
      dataIndex: 'prev_day_change',
      key: 'prev_day_change',
      width: 100,
      align: 'right',
      render: (change, record) => {
        if (change === null || change === undefined) {
          return <Text type="secondary">-</Text>;
        }
        
        const isPositive = change >= 0;
        const changePercent = record.prev_day_change_percent;
        
        return (
          <div>
            <div style={{ color: isPositive ? '#52c41a' : '#ff4d4f' }}>
              {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              {' '}₩{Math.abs(change).toLocaleString()}
            </div>
            {changePercent !== null && changePercent !== undefined && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ({isPositive ? '+' : ''}{changePercent.toFixed(2)}%)
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: '당일 손익',
      key: 'daily_pnl',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const change = record.prev_day_change;
        const quantity = record.quantity;
        
        if (change === null || change === undefined || !quantity) {
          return <Text type="secondary">-</Text>;
        }
        
        const dailyPnL = change * quantity;
        const isPositive = dailyPnL >= 0;
        
        return (
          <div style={{ color: isPositive ? '#52c41a' : '#ff4d4f' }}>
            <div style={{ fontWeight: 'bold' }}>
              {isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              {' '}₩{Math.abs(dailyPnL).toLocaleString()}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ({quantity.toLocaleString()}주)
            </Text>
          </div>
        );
      },
    },
    {
      title: '매수 원금',
      key: 'purchase_amount',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const purchaseAmount = (record.avg_buy_price || 0) * (record.quantity || 0);
        return purchaseAmount ? `₩${Math.round(purchaseAmount).toLocaleString()}` : '-';
      },
    },
    {
      title: '평가금액',
      dataIndex: 'evaluation_amount',
      key: 'evaluation_amount',
      width: 120,
      align: 'right',
      render: (amount) => amount ? `₩${Math.round(amount).toLocaleString()}` : '-',
    },
    {
      title: '손익',
      dataIndex: 'profit_loss',
      key: 'profit_loss',
      width: 100,
      align: 'right',
      render: (pnl, record) => {
        const isProfit = pnl >= 0;
        return (
          <div>
            <div style={{ color: isProfit ? '#52c41a' : '#ff4d4f' }}>
              {isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              {' '}₩{Math.round(Math.abs(pnl || 0)).toLocaleString()}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ({record.profit_loss_percent?.toFixed(2) || 0}%)
            </Text>
          </div>
        );
      },
    },
  ];

  const todayPnL = getTodayPnL();

  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <BankOutlined style={{ color: account.color }} />
              <span>{account.accountName}</span>
              <Tag color={account.color}>{account.brokerName}</Tag>
              {isLoading && (
                <Tag color="processing">로딩 중</Tag>
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              {accountTimestamp && !isLoading && (
                <Tooltip title={`데이터 조회 시간: ${new Date(accountTimestamp).toLocaleString()}`}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {new Date(accountTimestamp).toLocaleTimeString()}
                  </Text>
                </Tooltip>
              )}
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                loading={isRefreshing}
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh(account);
                }}
                title={`${account.accountName} 새로고침`}
                style={{
                  color: account.color,
                  border: 'none',
                  boxShadow: 'none'
                }}
              >
                새로고침
              </Button>
            </Space>
          </Col>
        </Row>
      }
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>계좌 정보를 불러오는 중...</Text>
          </div>
        </div>
      ) : hasError ? (
        <Alert
          message="계좌 정보 조회 실패"
          description={accountData.error}
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      ) : (
        <>
          {/* 계좌 잔고 정보 */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
              {hideAmounts ? (
                <div>
                  <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>총 자산</div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <DollarOutlined style={{ marginRight: '8px', fontSize: '20px' }} />
                    <span style={{ fontSize: '24px', fontWeight: 'bold' }}>******</span>
                  </div>
                </div>
              ) : (
                <Statistic
                  title="총 자산"
                  value={accountData.total_balance || 0}
                  precision={0}
                  prefix={<DollarOutlined />}
                  suffix="₩"
                />
              )}
            </Col>
            <Col xs={24} sm={12} md={6}>
              {hideAmounts ? (
                <div>
                  <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>가용 자금</div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <DollarOutlined style={{ marginRight: '8px', fontSize: '20px' }} />
                    <span style={{ fontSize: '24px', fontWeight: 'bold' }}>******</span>
                  </div>
                </div>
              ) : (
                <div>
                  <Statistic
                    title="가용 자금"
                    value={accountData.available_balance || 0}
                    precision={0}
                    prefix={<DollarOutlined />}
                    suffix="₩"
                  />
                  {isManualCashEditable && (
                    <div style={{ marginTop: '4px' }}>
                      {isKisAccount && manualCmaBalance > 0 && (
                        <Text type="secondary" style={{ display: 'block', fontSize: '12px', lineHeight: 1.4 }}>
                          CMA: ₩{Math.round(manualCmaBalance).toLocaleString()}
                        </Text>
                      )}
                      <Button
                        size="small"
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => onEditManualCash(account)}
                        style={{ padding: 0 }}
                      >
                        {isKisAccount ? 'CMA 금액 입력' : '수동 입력'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Col>
            <Col xs={24} sm={12} md={6}>
              {hideAmounts ? (
                <div>
                  <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>금일 손익</div>
                  <div style={{ display: 'flex', alignItems: 'center', color: todayPnL >= 0 ? '#3f8600' : '#cf1322' }}>
                    {todayPnL >= 0 ? <ArrowUpOutlined style={{ marginRight: '8px', fontSize: '20px' }} /> : <ArrowDownOutlined style={{ marginRight: '8px', fontSize: '20px' }} />}
                    <span style={{ fontSize: '24px', fontWeight: 'bold' }}>******</span>
                  </div>
                </div>
              ) : (
                <Statistic
                  title="금일 손익"
                  value={todayPnL}
                  precision={0}
                  valueStyle={{
                    color: todayPnL >= 0 ? '#3f8600' : '#cf1322'
                  }}
                  prefix={
                    todayPnL >= 0 ? 
                    <ArrowUpOutlined /> : <ArrowDownOutlined />
                  }
                  suffix="₩"
                />
              )}
            </Col>
            <Col xs={24} sm={12} md={6}>
              {hideAmounts ? (
                <div>
                  <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>총 손익</div>
                  <div style={{ display: 'flex', alignItems: 'center', color: (accountData.total_profit_loss || 0) >= 0 ? '#3f8600' : '#cf1322' }}>
                    {(accountData.total_profit_loss || 0) >= 0 ? <ArrowUpOutlined style={{ marginRight: '8px', fontSize: '20px' }} /> : <ArrowDownOutlined style={{ marginRight: '8px', fontSize: '20px' }} />}
                    <span style={{ fontSize: '24px', fontWeight: 'bold' }}>******</span>
                  </div>
                </div>
              ) : (
                <Statistic
                  title="총 손익"
                  value={accountData.total_profit_loss || 0}
                  precision={0}
                  valueStyle={{
                    color: (accountData.total_profit_loss || 0) >= 0 ? '#3f8600' : '#cf1322'
                  }}
                  prefix={
                    (accountData.total_profit_loss || 0) >= 0 ? 
                    <ArrowUpOutlined /> : <ArrowDownOutlined />
                  }
                  suffix="₩"
                />
              )}
            </Col>
          </Row>

          <Divider />

          {/* 퇴직연금 계좌 위험자산 비율 표시 */}
          {holdingsData?.retirement_account_info && (() => {
            const info = holdingsData.retirement_account_info;
            const riskyAsset = info.risky_asset_evaluation;
            const safeAsset = info.safe_asset_evaluation;
            
            // 70% 이내로 맞추기 위한 계산
            let safeAssetToAdd = 0;
            let riskyAssetToReduce = 0;
            
            if (info.is_over_limit) {
              safeAssetToAdd = Math.max(0, Math.ceil((3 * riskyAsset - 7 * safeAsset) / 7));
              riskyAssetToReduce = Math.max(0, Math.ceil((3 * riskyAsset - 7 * safeAsset) / 3));
            }
            
            return (
              <div style={{ marginBottom: '24px' }}>
                <Alert
                  message={
                    <span>
                      <strong>{info.account_type} 계좌 위험자산 비율</strong>
                    </span>
                  }
                  description={
                    <div>
                      <Row gutter={[16, 8]}>
                        <Col span={24}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: info.is_over_limit ? '#ff4d4f' : '#52c41a' }}>
                            {info.risky_asset_ratio}%
                          </div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">위험자산</Text>
                          <div>₩{riskyAsset.toLocaleString()}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">안전자산</Text>
                          <div>₩{safeAsset.toLocaleString()}</div>
                        </Col>
                        <Col span={24}>
                          <Text type="secondary">총 평가액</Text>
                          <div>₩{info.total_evaluation.toLocaleString()}</div>
                        </Col>
                      </Row>
                      {info.is_over_limit && (
                        <>
                          <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fff2f0', borderRadius: '4px' }}>
                            <Text type="danger">
                              ⚠️ 위험자산 비율이 70%를 초과했습니다. 추가 위험자산 매수가 불가능합니다.
                            </Text>
                          </div>
                          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '4px', border: '1px solid #91d5ff' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#0050b3' }}>
                              💡 70% 이내로 맞추는 방법
                            </div>
                            <Row gutter={[8, 8]}>
                              <Col span={24}>
                                <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ color: '#52c41a', marginRight: '8px' }}>✓</span>
                                  <Text>
                                    <strong>안전자산 매수:</strong> ₩{safeAssetToAdd.toLocaleString()} 이상
                                  </Text>
                                </div>
                              </Col>
                              <Col span={24}>
                                <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ color: '#ff4d4f', marginRight: '8px' }}>✓</span>
                                  <Text>
                                    <strong>위험자산 매도:</strong> ₩{riskyAssetToReduce.toLocaleString()} 이상
                                  </Text>
                                </div>
                              </Col>
                            </Row>
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                              <Text type="secondary">
                                * 안전자산(채권, MMF 등)을 추가 매수하거나 위험자산(주식, 펀드 등)을 일부 매도하세요.
                              </Text>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  }
                  type={info.is_over_limit ? 'warning' : 'info'}
                  showIcon
                />
              </div>
            );
          })()}

          {/* 보유 주식 정보 */}
          <div>
            <Title level={5}>
              <StockOutlined /> 보유 주식 ({holdings.length || 0}개)
            </Title>
            
            {holdings.length > 0 ? (
              <Table
                columns={holdingsColumns}
                dataSource={holdings}
                rowKey={(record) => record.symbol || Math.random()}
                pagination={false}
                size="small"
                scroll={{ x: 600 }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <StockOutlined style={{ fontSize: '24px', color: '#d9d9d9' }} />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary">보유 주식이 없습니다</Text>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default AccountDetailCard;
